<template>
    <skeleton-loader v-if="loading || completed" type="wellness" />
    <div v-else class="wellness">
        <div class="wellness-dots">
            <span v-for="(q, i) in currentQuestions" :key="i" class="wellness-dot"
                :class="{ active: i === carousel, done: i < carousel }" />
        </div>

        <div class="wellness-hero">
            <div class="wellness-step-num">{{ carousel + 1 }} / {{ currentQuestions.length }}</div>
            <div class="wellness-question">{{ currentQuestion.title }}</div>
            <div v-if="currentQuestion.type === 'body'" class="wellness-hint">{{ $t('questions.select_pain_zones') }}</div>
        </div>

        <transition :name="transitionName" mode="out-in">
            <component :is="typeComponent" :key="carousel" :question="currentQuestion" class="wellness-step"
                @click:next="nextCarousel" />
        </transition>
    </div>
</template>

<script>

import { db } from "@/utils/Firebase";
import { getQuestions } from "../../utils/LocalData";
import ScaleType from '../elements/questions/ScaleType.vue';
import ShortType from '../elements/questions/ShortType.vue';
import SliderType from '../elements/questions/SliderType.vue';
import BodyType from '../elements/questions/BodyType.vue';
import SliderVerticalType from '../elements/questions/SliderVerticalType.vue';
import StatisticType from '../elements/questions/StatisticType.vue';
import { getWellnessIcon, sumWellnessPreInfo, updateWellness, wellnessDefaultColors, wellnessQuestionsCount } from "../../utils/Routine";
import { sendMessage, MESSAGE_COLORS } from "../../utils/Messages";
import SkeletonLoader from '../common/SkeletonLoader.vue';

const TYPE_COMPONENT = {
    scale: 'scale-type',
    short: 'short-type',
    slider: 'slider-type',
    body: 'body-type',
    slider_vertical: 'slider-vertical-type',
    statistic: 'statistic-type',
}

export default {
    components: { ScaleType, ShortType, SliderType, BodyType, SliderVerticalType, StatisticType, SkeletonLoader },
    name: "PreAssignedRoutine",
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        coachId: {
            type: String,
            default: null,
        },
        questionnaireId: {
            type: String,
            default: '',
        },
        isCompetence: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            completed: false,
            routine: null,
            carousel: 0,
            currentQuestions: getQuestions().slice(0, 5),
            preInfo: {},
            sumWellness: 0,
            wellness: null,
            preSummary: [],
            transitionName: 'wellness-next',
        }
    },
    computed: {
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        currentQuestion(vm = this) {
            return vm.currentQuestions[vm.carousel] || {}
        },
        typeComponent(vm = this) {
            return TYPE_COMPONENT[vm.currentQuestion.type] || 'short-type'
        },
    },
    mounted() {
        const vm = this;
        vm.loadQuestionnaire()
    },
    methods: {
        async loadQuestionnaire() {
            const vm = this;
            if (!vm.questionnaireId) return;
            await vm.withLoading(async () => {
                const questionnaireRef = await db.collection('questionnaires').doc(vm.questionnaireId).get()
                const savedQuestions = questionnaireRef?.data()?.questions || []

                const forced = getQuestions().filter(q => q.force)
                const forcedIds = new Set(forced.map(q => q.id))
                const nonForced = savedQuestions.filter(q => !forcedIds.has(q.id))

                vm.currentQuestions = [...forced, ...nonForced];
            }, 'loadingQuestions')
        },
        async start() {
            const vm = this
            vm.completed = true
            try {
                await vm.withLoading(async () => {
                    const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                        'advance.pre': true,
                        'advance.preInfo': vm.preInfo,
                        'advance.preSummary': vm.preSummary,
                    })
                    if (!saved) {
                        vm.completed = false
                        return
                    }

                    vm.sumWellness = sumWellnessPreInfo(vm.preInfo)
                    vm.wellness = await updateWellness(vm.sumWellness, vm.preInfo)
                    await vm.sendMessageToCoach()

                    vm.$emit('complete')
                })
            } catch (e) {
                vm.completed = false
            }
        },
        nextCarousel(question, option) {
            const vm = this

            const attribute = question.value
            const preInfoAttributes = ['sleep_night', 'muscles_hurt', 'tired', 'stressed', 'energy']

            if (Array.isArray(option.values)) {
                const payload = { reference: question.reference, value: option.values, type: question.type }
                if (option.comment) payload.comment = option.comment
                if (option.zones) payload.zones = option.zones
                vm.preSummary.push(payload)
            } else {
                const answer = option.value
                if (preInfoAttributes.includes(attribute)) vm.preInfo[attribute] = answer

                const payload = { reference: question.reference, value: answer, type: question.type }
                if (option.comment) payload.comment = option.comment
                if (option.type === 'statistic') payload.isStatistic = true

                vm.preSummary.push(payload)
            }

            vm.transitionName = 'wellness-next'
            vm.carousel === vm.currentQuestions.length - 1 ? vm.start() : vm.carousel++
        },
        async sendMessageToCoach() {
            const vm = this

            let wellnessColor = getWellnessIcon(vm.wellness)
            let message = `${vm.$t('common.date')}: ${vm.$moment().format('DD-MM-YYYY')} \n${vm.$t('common.wellness')}: ${vm.sumWellness / wellnessQuestionsCount}`

            let payload = {
                message: message,
                from: vm.studentId,
                assignedRoutineId: vm.assignedRoutineId,
                preSummary: vm.preSummary,
                isCompetence: vm.isCompetence
            }

            if ([wellnessDefaultColors.bad, wellnessDefaultColors.veryBad].includes(wellnessColor)) payload.color = MESSAGE_COLORS.wellnessBad

            await sendMessage(payload, vm.coachId, vm.studentId)
        },
    },
    watch: {
        questionnaireId() {
            this.loadQuestionnaire()
        }
    }
}
</script>

<style scoped>
.wellness {
    display: flex;
    flex-direction: column;
    min-height: clamp(420px, 62vh, 620px);
    max-width: 640px;
    margin: 0 auto;
    width: 100%;
    padding: 0 16px;
}

.wellness-dots {
    display: flex;
    justify-content: center;
    gap: 6px;
    padding: 8px 0 4px;
    flex-shrink: 0;
}

.wellness-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--v-outline-base);
    transition: background 0.3s, transform 0.3s;
}

.wellness-dot.active {
    background: var(--v-accent-base);
    transform: scale(1.4);
}

.theme--dark .wellness-dot.active {
    background: var(--v-primary-base);
}

.wellness-dot.done {
    background: var(--v-accent-base);
    opacity: 0.4;
}

.theme--dark .wellness-dot.done {
    background: var(--v-primary-base);
}

.wellness-hero {
    flex-shrink: 0;
    text-align: center;
    padding: 14px 0 10px;
}

.wellness-step-num {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
    margin-bottom: 6px;
}

.wellness-question {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    line-height: 1.25;
}

.wellness-hint {
    font-size: 13px;
    color: var(--v-txtMuted-base);
    margin-top: 6px;
}

.wellness-step {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.wellness-next-enter-active,
.wellness-next-leave-active {
    transition: opacity 0.2s, transform 0.2s;
}

.wellness-next-enter {
    opacity: 0;
    transform: translateY(16px);
}

.wellness-next-leave-to {
    opacity: 0;
    transform: translateY(-16px);
}

@media (max-width: 599px) {
    .wellness {
        min-height: clamp(380px, 66vh, 560px);
    }

    .wellness-question {
        font-size: 19px;
    }
}
</style>
